import React, { useMemo, useState, useEffect } from 'react';
import Timeline from 'react-calendar-timeline';
import 'react-calendar-timeline/styles.css';
import dayjs from 'dayjs';
import './InteractiveTimeline.css';
import { CButton, CButtonGroup } from '@coreui/react';
import { useTranslation } from 'react-i18next';

const formatDuration = (ms) => {
    if (ms < 0) ms = 0;
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

const MIN_ZOOM = 60 * 1000; 
const MAX_ZOOM = 3 * 24 * 60 * 60 * 1000; 

const InteractiveTimeline = ({ timelineData, dateRange, onItemDoubleClick, classIdToMeta }) => {
    const { t, i18n } = useTranslation();
    const [visibleTimeStart, setVisibleTimeStart] = useState(null);
    const [visibleTimeEnd, setVisibleTimeEnd] = useState(null);
    const [isTimelineReady, setIsTimelineReady] = useState(false);

    // --- НОВЫЙ БЛОК: СИНХРОНИЗАЦИЯ ЯЗЫКА С БИБЛИОТЕКОЙ ---
    useEffect(() => {
        // Timeline экспортирует статический метод setDayjsLocale.
        // Мы вызываем его, чтобы переключить внутренний dayjs библиотеки.
        // Важно: мы добавили 'ru' и 'en' в js файл библиотеки на шаге 1.
        if (Timeline && Timeline.setDayjsLocale) {
            Timeline.setDayjsLocale(i18n.language);
        }
    }, [i18n.language]);
    // ----------------------------------------------------

    useEffect(() => {
        setIsTimelineReady(false);

        if (!dateRange || !dateRange.start || !dateRange.end) {
            return;
        }

        const fullRangeStart = dayjs(dateRange.start);
        const fullRangeEnd = dayjs(dateRange.end);

        if (timelineData && timelineData.length > 0) {
            const firstItemStart = dayjs(timelineData[0].start);
            const initialZoomDuration = 4 * 60 * 60 * 1000; 

            let newVisibleStart = firstItemStart.valueOf();
            let newVisibleEnd = newVisibleStart + initialZoomDuration;

            if (newVisibleEnd > fullRangeEnd.valueOf()) {
                newVisibleEnd = fullRangeEnd.valueOf();
            }

            setVisibleTimeStart(newVisibleStart);
            setVisibleTimeEnd(newVisibleEnd);
        } else {
            setVisibleTimeStart(fullRangeStart.valueOf());
            setVisibleTimeEnd(fullRangeEnd.valueOf());
        }

        const timer = setTimeout(() => setIsTimelineReady(true), 50);
        return () => clearTimeout(timer);

    }, [dateRange, timelineData]);


    const { groups, items } = useMemo(() => {
        if (!timelineData || timelineData.length === 0) {
            return { groups: [], items: [] };
        }

        const timelineGroups = [{ id: 1, title: t('common.post') || 'Post' }]; // Используем перевод, если есть

        const timelineItems = timelineData.map((segment, index) => {
            const meta = classIdToMeta[segment.class_id] || classIdToMeta.default;
            const startTime = dayjs(segment.start);
            const endTime = dayjs(segment.end);
            const durationMs = endTime.diff(startTime);

            let title = `${meta.label}: ${formatDuration(durationMs)}`;
            if (segment.people_count > 0) {
                // Используем интерполяцию для перевода, если ключ поддерживает параметры
                title = `${meta.label} (max ${segment.people_count}): ${formatDuration(durationMs)}`;
            }

            return {
                id: index,
                group: 1,
                title: title,
                start_time: startTime.valueOf(),
                end_time: endTime.valueOf(),
                canMove: false,
                canResize: false,
                itemProps: {
                    style: {
                        background: meta.color,
                        border: 'none',
                        color: (segment.class_id === -1 || segment.class_id === -2) ? '#333' : 'white',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer'
                    }
                }
            };
        });

        return { groups: timelineGroups, items: timelineItems };
    }, [timelineData, classIdToMeta, t]);

    const handleTimeChange = (visibleTimeStart, visibleTimeEnd) => {
        setVisibleTimeStart(visibleTimeStart);
        setVisibleTimeEnd(visibleTimeEnd);
    };
    
    const handleZoom = (factor) => {
        if (visibleTimeStart === null || visibleTimeEnd === null || !dateRange || !dateRange.start || !dateRange.end) {
            return;
        }

        const fullRangeStart = dayjs(dateRange.start).valueOf();
        const fullRangeEnd = dayjs(dateRange.end).valueOf();
        const totalDuration = fullRangeEnd - fullRangeStart;

        const visibleTime = visibleTimeEnd - visibleTimeStart;
        let newVisibleTime = visibleTime * factor;
        
        if (newVisibleTime < MIN_ZOOM) newVisibleTime = MIN_ZOOM;
        if (newVisibleTime > MAX_ZOOM) newVisibleTime = MAX_ZOOM;
        if (newVisibleTime > totalDuration) newVisibleTime = totalDuration;
        
        const center = visibleTimeStart + visibleTime / 2;
        let newVisibleStart = center - newVisibleTime / 2;
        
        if (newVisibleStart < fullRangeStart) newVisibleStart = fullRangeStart;
        if (newVisibleStart + newVisibleTime > fullRangeEnd) newVisibleStart = fullRangeEnd - newVisibleTime;

        const newVisibleEnd = newVisibleStart + newVisibleTime;
        
        setVisibleTimeStart(newVisibleStart);
        setVisibleTimeEnd(newVisibleEnd);
    };

    if (!timelineData) {
        return null;
    }

    if (items.length === 0) {
        return <p className="text-center text-muted p-3">{t('camera.no_videos')}</p>;
    }

    if (!isTimelineReady) {
        return <div className="text-center p-3 text-muted">{t('timeline.loading')}</div>;
    }

    return (
        <div>
            <div className="timeline-controls">
                <CButtonGroup role="group" aria-label="Timeline zoom controls">
                    <CButton color="secondary" variant="outline" onClick={() => handleZoom(1.5)}>–</CButton>
                    <CButton color="secondary" variant="outline" onClick={() => handleZoom(0.5)}>+</CButton>
                </CButtonGroup>
                <span className="timeline-hint">
                    {t('timeline.zoom_hint')}
                </span>
            </div>
            <Timeline
                groups={groups}
                items={items}
                visibleTimeStart={visibleTimeStart}
                visibleTimeEnd={visibleTimeEnd}
                onTimeChange={handleTimeChange}
                onItemDoubleClick={onItemDoubleClick}
                sidebarWidth={0}
                lineHeight={50}
                itemHeightRatio={0.75}
                canChangeGroup={false}
                wheelZoomModifierKey="Ctrl"
                minZoom={MIN_ZOOM}
                maxZoom={MAX_ZOOM}
                buffer={1}
            />
        </div>
    );
};

export default InteractiveTimeline;